import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box, useTheme, Skeleton } from '@mui/material'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { useDeviceData } from './hooks/useDeviceData'
import { getWidgetColors } from './utils/helpers'
import DeviceHeader from './components/DeviceHeader'
import DeviceStatusBar from './components/DeviceStatusBar'
import DeviceDetailsPanel from './components/DeviceDetailsPanel'
import { getStatusConfig as getStatusConfigHelper } from './utils/constants'

/**
 * Refactored Manufacturing Widget
 * Displays the status and details of a selected device
 */
const ManufacturingWidget = ({ w, data }) => {
  const theme = useTheme()
  const { t_ } = useSmartTranslation()

  // Derived props
  const showImage = w >= 4
  const selectedDevice = data?.device
  const showPanel = data?.showPanel ?? true

  // Memoized colors
  const colors = useMemo(() => getWidgetColors(theme), [theme])

  // Device data fetching
  const { data: deviceData, loading } = useDeviceData(selectedDevice?.id, showPanel)

  // Status configuration
  const statusConfig = useMemo(() =>
    getStatusConfigHelper(deviceData?.status, theme, t_),
  [deviceData?.status, theme, t_]
  )

  if (!selectedDevice) {
    return (
      <Box sx={{ p: 2, color: colors.textLight, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {t_('Please select a device in configuration')}
      </Box>
    )
  }

  return (
    <Box
      sx={{
        backgroundColor: colors.bgDark,
        color: colors.textLight,
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        cursor: 'pointer'
      }}
    >
      {/* 1. Header */}
      <DeviceHeader
        deviceName={selectedDevice.name}
        mode={deviceData?.mode}
        colors={colors}
      />

      {/* 2. Status Bar */}
      {loading && !deviceData
        ? (
          <Skeleton variant='rectangular' height={40} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
          )
        : (
          <DeviceStatusBar
            statusConfig={statusConfig}
            statusText={statusConfig.label}
          />
          )}

      {/* 3. Details Panel */}
      {showPanel && (
        loading && !deviceData
          ? (
            <Box sx={{ p: 2 }}>
              <Skeleton variant='rectangular' height={100} sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />
            </Box>
            )
          : (
              deviceData && (
                <DeviceDetailsPanel
                  showImage={showImage}
                  data={deviceData.rows}
                  colors={colors}
                  theme={theme}
                  t_={t_}
                />
              )
            )
      )}
    </Box>
  )
}

ManufacturingWidget.propTypes = {
  w: PropTypes.number,
  data: PropTypes.shape({
    device: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string
    }),
    showPanel: PropTypes.bool
  })
}

export default ManufacturingWidget
